<template>
  <NavBar pageName="Products"></NavBar>
  <v-container style="margin-top: 185px;">
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2">mdi-package-variant</v-icon>
        Distributor Products
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="openCreateDialog">
          <v-icon start>mdi-plus</v-icon>
          Add Product
        </v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Filters -->
        <v-row class="mb-4">
          <v-col cols="12" md="4">
            <v-autocomplete
              v-model="selectedDistributor"
              :items="distributorClients"
              item-title="name"
              item-value="id"
              label="Filter by Distributor"
              clearable
              density="compact"
              variant="outlined"
              @update:model-value="loadProducts"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="searchTerm"
              label="Search by name"
              clearable
              density="compact"
              variant="outlined"
              prepend-inner-icon="mdi-magnify"
              @keyup.enter="loadProducts"
              @click:clear="searchTerm = ''; loadProducts()"
              @click="$event.target.select()"
              @dblclick="searchTerm = ''"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4" class="d-flex align-center">
            <v-btn color="primary" variant="outlined" @click="loadProducts">
              <v-icon start>mdi-refresh</v-icon>
              Refresh
            </v-btn>
          </v-col>
        </v-row>

        <!-- Products Table -->
        <v-data-table
          :headers="headers"
          :items="products"
          :loading="loading"
          item-value="id"
          class="elevation-1"
        >
          <template v-slot:[`item.billing_i_product`]="{ item }">
            <code v-if="item.billing_i_product">{{ item.billing_i_product }}</code>
            <span v-else class="text-grey">-</span>
          </template>

          <template v-slot:[`item.created_at`]="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
              icon
              size="small"
              variant="text"
              color="primary"
              @click="openEditDialog(item)"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              icon
              size="small"
              variant="text"
              color="info"
              @click="openAllocateDialog(item)"
            >
              <v-icon>mdi-account-multiple-plus</v-icon>
            </v-btn>
            <v-btn
              v-if="isRoot"
              icon
              size="small"
              variant="text"
              color="error"
              title="Delete Product (Admin only)"
              @click="confirmDelete(item)"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Product Allocations Section -->
    <v-card class="mt-6">
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2">mdi-link-variant</v-icon>
        Product Allocations
        <v-spacer></v-spacer>
        <v-btn color="success" class="mr-2" @click="openQuickAllocateDialog">
          <v-icon start>mdi-link-variant</v-icon>
          Allocate Product
        </v-btn>
        <v-btn color="primary" variant="outlined" @click="loadAllocations">
          <v-icon start>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Allocation Filters -->
        <v-row class="mb-4">
          <v-col cols="12" md="4">
            <v-autocomplete
              v-model="allocationFilterDistributor"
              :items="distributorClients"
              item-title="name"
              item-value="id"
              label="Filter by Distributor"
              clearable
              density="compact"
              variant="outlined"
              @update:model-value="loadAllocations"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" md="4">
            <v-autocomplete
              v-model="allocationFilterProduct"
              :items="products"
              item-title="name"
              item-value="id"
              label="Filter by Product"
              clearable
              density="compact"
              variant="outlined"
              @update:model-value="loadAllocations"
            ></v-autocomplete>
          </v-col>
        </v-row>

        <!-- Allocations Table -->
        <v-data-table
          :headers="allocationHeaders"
          :items="allocations"
          :loading="loadingAllocationsList"
          item-value="id"
          class="elevation-1"
        >
          <template v-slot:[`item.created_at`]="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
              icon
              size="small"
              variant="text"
              color="error"
              title="Remove allocation (does not delete product)"
              @click="confirmDeallocate(item)"
            >
              <v-icon>mdi-link-variant-off</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Service-Product Mappings Section -->
    <v-card class="mt-6">
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2">mdi-link-box-variant</v-icon>
        Service-Product Mappings
        <v-chip size="small" class="ml-2">{{ serviceMappings.length }}</v-chip>
        <v-spacer></v-spacer>
        <v-btn color="success" class="mr-2" @click="openAddServiceMappingDialog">
          <v-icon start>mdi-plus</v-icon>
          Add Mapping
        </v-btn>
        <v-btn color="primary" variant="outlined" @click="loadServiceMappings">
          <v-icon start>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </v-card-title>

      <v-card-text>
        <p class="text-caption text-grey mb-3">
          Maps services to billing products for each distributor. Used to determine <code>i_product</code> when calling MVNO API.
        </p>

        <!-- Service Mappings Filters -->
        <v-row class="mb-4">
          <v-col cols="12" md="4">
            <v-autocomplete
              v-model="serviceMappingFilterDistributor"
              :items="distributorClients"
              item-title="name"
              item-value="id"
              label="Filter by Distributor"
              clearable
              density="compact"
              variant="outlined"
              @update:model-value="loadServiceMappings"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" md="4">
            <v-autocomplete
              v-model="serviceMappingFilterProduct"
              :items="products"
              item-title="name"
              item-value="id"
              label="Filter by Product"
              clearable
              density="compact"
              variant="outlined"
              @update:model-value="loadServiceMappings"
            ></v-autocomplete>
          </v-col>
        </v-row>

        <!-- Service Mappings Table -->
        <v-data-table
          :headers="serviceMappingHeaders"
          :items="serviceMappings"
          :loading="loadingServiceMappings"
          item-value="id"
          class="elevation-1"
          :items-per-page="10"
        >
          <template v-slot:[`item.service_name`]="{ item }">
            <div>
              <strong>{{ item.service_name }}</strong>
              <div class="text-caption text-grey">ID: {{ item.service_id }}</div>
            </div>
          </template>

          <template v-slot:[`item.distributor_name`]="{ item }">
            <div>
              <strong>{{ item.distributor_name }}</strong>
              <div class="text-caption text-grey">ID: {{ item.distributor_id }}</div>
            </div>
          </template>

          <template v-slot:[`item.product_name`]="{ item }">
            <div>
              <strong>{{ item.product_name }}</strong>
              <div class="text-caption text-grey">
                <v-chip size="x-small" color="primary">i_product: {{ item.i_product || 'N/A' }}</v-chip>
              </div>
            </div>
          </template>

          <template v-slot:[`item.mvno_id`]="{ item }">
            <v-chip v-if="item.mvno_id" size="small" color="info" variant="outlined">
              {{ item.mvno_id }}
            </v-chip>
            <span v-else class="text-grey text-caption">All</span>
          </template>

          <template v-slot:[`item.active`]="{ item }">
            <v-chip
              :color="item.active ? 'success' : 'error'"
              size="small"
              @click="toggleServiceMappingActive(item)"
              style="cursor: pointer;"
            >
              {{ item.active ? 'Active' : 'Inactive' }}
            </v-chip>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
              icon
              size="small"
              variant="text"
              color="primary"
              @click="openEditServiceMappingDialog(item)"
              title="Edit mapping"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              icon
              size="small"
              variant="text"
              color="error"
              @click="confirmDeleteServiceMapping(item)"
              title="Delete mapping"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>

          <template v-slot:no-data>
            <v-alert type="info" density="compact" class="ma-2">
              No service-product mappings found
            </v-alert>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Service Mapping Add/Edit Dialog -->
    <DistributorServiceProductDialog
      :show="showServiceMappingDialog"
      :is-editing="isEditingServiceMapping"
      :form-data="serviceMappingForm"
      :distributors="distributorClients"
      :token="token"
      :saving="savingServiceMapping"
      @close="showServiceMappingDialog = false"
      @save="saveServiceMapping"
      @show-snackbar="handleServiceMappingSnackbar"
    />

    <!-- Delete Service Mapping Confirmation Dialog -->
    <v-dialog v-model="showDeleteServiceMappingDialog" max-width="400">
      <v-card>
        <v-card-title class="bg-error text-white">
          <v-icon class="mr-2">mdi-delete</v-icon>
          Delete Service-Product Mapping
        </v-card-title>
        <v-card-text class="pt-4">
          <p>Are you sure you want to delete this mapping?</p>
          <div class="pa-3 bg-grey-lighten-4 rounded mt-2" v-if="deletingServiceMapping">
            <div><strong>Service:</strong> {{ deletingServiceMapping.service_name }}</div>
            <div><strong>Distributor:</strong> {{ deletingServiceMapping.distributor_name }}</div>
            <div><strong>Product:</strong> {{ deletingServiceMapping.product_name }} (i_product: {{ deletingServiceMapping.i_product }})</div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteServiceMappingDialog = false">Cancel</v-btn>
          <v-btn color="error" :loading="deletingServiceMappingLoading" @click="executeDeleteServiceMapping">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Deallocate Confirmation Dialog -->
    <v-dialog v-model="showDeallocateDialog" max-width="400">
      <v-card>
        <v-card-title class="bg-warning">
          <v-icon class="mr-2">mdi-link-variant-off</v-icon>
          Remove Allocation
        </v-card-title>
        <v-card-text class="pt-4">
          <p>Remove product allocation from distributor?</p>
          <div class="pa-3 bg-grey-lighten-4 rounded mt-2">
            <div><strong>Product:</strong> {{ deallocatingItem?.product_name }}</div>
            <div><strong>Distributor:</strong> {{ deallocatingItem?.distributor_name }}</div>
          </div>
          <v-alert type="info" variant="tonal" density="compact" class="mt-3">
            This only removes the allocation. The product will NOT be deleted.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeallocateDialog = false">Cancel</v-btn>
          <v-btn color="warning" :loading="deallocating" @click="executeDeallocate">
            Remove Allocation
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Create/Edit Product Dialog -->
    <v-dialog v-model="showProductDialog" max-width="500">
      <v-card>
        <v-card-title>
          {{ editingProduct ? 'Edit Product' : 'Create Product' }}
        </v-card-title>
        <v-card-text>
          <v-form ref="productForm" v-model="formValid">
            <v-text-field
              v-model="productForm.name"
              label="Product Name"
              :rules="[v => !!v || 'Name is required']"
              required
              density="compact"
              variant="outlined"
              class="mb-3"
            ></v-text-field>

            <v-text-field
              v-model="productForm.billing_i_product"
              label="Billing i_product (MVNO ID)"
              type="number"
              density="compact"
              variant="outlined"
              class="mb-3"
            ></v-text-field>

            <v-divider class="mb-3" />
            <div class="text-subtitle-2 mb-2">Trafic inclus in plan</div>

            <v-text-field
              v-model="productForm.included_data_mb"
              label="Date incluse (MB)"
              type="number"
              density="compact"
              variant="outlined"
              class="mb-3"
              hint="Gol = nelimitat"
              persistent-hint
              clearable
            ></v-text-field>

            <v-text-field
              v-model="productForm.included_sms_count"
              label="SMS incluse (numar)"
              type="number"
              density="compact"
              variant="outlined"
              class="mb-3"
              hint="Gol = nelimitat"
              persistent-hint
              clearable
            ></v-text-field>

            <v-text-field
              v-model="productForm.included_voice_minutes"
              label="Minute voce incluse"
              type="number"
              density="compact"
              variant="outlined"
              class="mb-3"
              hint="Gol = nelimitat"
              persistent-hint
              clearable
            ></v-text-field>

            <v-autocomplete
              v-if="!editingProduct"
              v-model="productForm.reseller_id"
              :items="distributorClientsForOwner"
              item-title="name"
              item-value="id"
              label="Owner Distributor"
              :rules="[v => !!v || 'Distributor is required']"
              required
              density="compact"
              variant="outlined"
            ></v-autocomplete>

            <div v-if="editingProduct" class="text-caption text-grey">
              Owner: {{ editingProduct.reseller_name }}
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showProductDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            :disabled="!formValid"
            @click="saveProduct"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Allocate to Distributors Dialog -->
    <v-dialog v-model="showAllocateDialog" max-width="600">
      <v-card>
        <v-card-title>
          Allocate Product to Distributors
        </v-card-title>
        <v-card-subtitle v-if="allocatingProduct">
          {{ allocatingProduct.name }}
        </v-card-subtitle>
        <v-card-text>
          <v-progress-linear v-if="loadingAllocations" indeterminate></v-progress-linear>

          <div v-else>
            <!-- Current Allocations -->
            <div class="mb-4">
              <div class="text-subtitle-2 mb-2">Currently Allocated To:</div>
              <div v-if="currentAllocations.length === 0" class="text-grey text-caption">
                Not allocated to any distributors yet.
              </div>
              <v-chip
                v-for="alloc in currentAllocations"
                :key="alloc.id"
                class="mr-2 mb-2"
                closable
                @click:close="deallocate(alloc.id)"
              >
                {{ alloc.name }}
              </v-chip>
            </div>

            <v-divider class="my-4"></v-divider>

            <!-- Add New Allocation -->
            <div class="text-subtitle-2 mb-2">Add Allocation:</div>
            <v-row>
              <v-col cols="8">
                <v-autocomplete
                  v-model="newAllocationDistributorId"
                  :items="availableDistributorsForAllocation"
                  item-title="name"
                  item-value="id"
                  label="Select Distributor"
                  density="compact"
                  variant="outlined"
                  clearable
                ></v-autocomplete>
              </v-col>
              <v-col cols="4">
                <v-btn
                  color="primary"
                  :disabled="!newAllocationDistributorId"
                  :loading="allocating"
                  @click="allocateToDistributor"
                >
                  Allocate
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" color="red" @click="showAllocateDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Quick Allocate Product Dialog -->
    <v-dialog v-model="showQuickAllocateDialog" max-width="500">
      <v-card>
        <v-card-title class="bg-success text-white">
          <v-icon class="mr-2">mdi-link-variant</v-icon>
          Allocate Product to Distributor
        </v-card-title>
        <v-card-text class="pt-4">
          <v-autocomplete
            v-model="quickAllocateDistributorId"
            :items="allDistributors"
            item-title="name"
            item-value="id"
            label="Select Distributor *"
            density="compact"
            variant="outlined"
            class="mb-4"
            :rules="[v => !!v || 'Distributor is required']"
          ></v-autocomplete>

          <v-autocomplete
            v-model="quickAllocateProductId"
            :items="allProducts"
            item-title="name"
            item-value="id"
            label="Select Product *"
            density="compact"
            variant="outlined"
            :rules="[v => !!v || 'Product is required']"
          >
            <template v-slot:item="{ item, props }">
              <v-list-item v-bind="props">
                <template v-slot:subtitle>
                  <span v-if="item.raw.billing_i_product">Billing ID: {{ item.raw.billing_i_product }}</span>
                </template>
              </v-list-item>
            </template>
          </v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showQuickAllocateDialog = false">Cancel</v-btn>
          <v-btn
            color="success"
            :loading="quickAllocating"
            :disabled="!quickAllocateDistributorId || !quickAllocateProductId"
            @click="executeQuickAllocate"
          >
            Allocate
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="450">
      <v-card>
        <v-card-title class="bg-error text-white">
          <v-icon class="mr-2">mdi-alert</v-icon>
          Delete Product (Permanent!)
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" variant="tonal" class="mb-3">
            <strong>Warning:</strong> This will permanently delete the product AND all its allocations to distributors!
          </v-alert>
          <p>Are you sure you want to delete product:</p>
          <div class="pa-3 bg-grey-lighten-4 rounded mt-2">
            <strong>{{ deletingProduct?.name }}</strong>
            <div class="text-caption">ID: {{ deletingProduct?.id }}</div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" :loading="deleting" @click="deleteProduct">
            <v-icon class="mr-1">mdi-delete</v-icon>
            Delete Permanently
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import NavBar from "@/components/NavBar.vue";
import DistributorServiceProductDialog from "@/components/dialogs/DistributorServiceProductDialog.vue";
import { log } from "@/composables/useLog";
import useGetLoggedIn from "@/composables/useGetLoggedIn";
import {
  getProducts,
  getProductDetails,
  createProduct,
  updateProduct,
  deleteProduct as deleteProductApi,
  allocateProductToDistributor,
  deallocateProductFromDistributor,
  getProductAllocations,
  getDistributorClients
} from "@/composables/useProductsAPI";
import {
  getDistributorServiceProducts,
  addDistributorServiceProduct,
  updateDistributorServiceProduct,
  deleteDistributorServiceProduct
} from "@/composables/useDistributorServiceProductsAPI";

export default defineComponent({
  name: 'DistributorProductsView',
  components: { NavBar, DistributorServiceProductDialog },
  data() {
    return {
      token: '',
      loading: false,
      products: [],
      distributorClients: [],

      // Filters
      selectedDistributor: null,
      searchTerm: '',

      // Table headers
      headers: [
        { title: 'ID', key: 'id', width: '70px' },
        { title: 'Name', key: 'name' },
        { title: 'Owner', key: 'reseller_name' },
        { title: 'Billing i_product', key: 'billing_i_product', width: '140px' },
        { title: 'Created', key: 'created_at', width: '120px' },
        { title: 'Actions', key: 'actions', sortable: false, width: '140px' }
      ],

      // Create/Edit dialog
      showProductDialog: false,
      editingProduct: null,
      formValid: false,
      saving: false,
      productForm: {
        name: '',
        billing_i_product: null,
        reseller_id: null,
        included_data_mb: null,
        included_sms_count: null,
        included_voice_minutes: null
      },

      // Allocate dialog
      showAllocateDialog: false,
      allocatingProduct: null,
      loadingAllocations: false,
      currentAllocations: [],
      newAllocationDistributorId: null,
      allocating: false,

      // Delete dialog
      showDeleteDialog: false,
      deletingProduct: null,
      deleting: false,

      // Quick Allocate dialog
      showQuickAllocateDialog: false,
      quickAllocateDistributorId: null,
      quickAllocateProductId: null,
      quickAllocating: false,
      allDistributors: [],
      allProducts: [],

      // Snackbar
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',

      // User
      currentUser: null,

      // Allocations section
      allocations: [],
      loadingAllocationsList: false,
      allocationFilterDistributor: null,
      allocationFilterProduct: null,
      allocationHeaders: [
        { title: 'ID', key: 'id', width: '70px' },
        { title: 'Product', key: 'product_name' },
        { title: 'Distributor', key: 'distributor_name' },
        { title: 'Created', key: 'created_at', width: '120px' },
        { title: 'Actions', key: 'actions', sortable: false, width: '100px' }
      ],
      showDeallocateDialog: false,
      deallocatingItem: null,
      deallocating: false,

      // Service-Product Mappings section
      serviceMappings: [],
      loadingServiceMappings: false,
      serviceMappingFilterDistributor: null,
      serviceMappingFilterProduct: null,
      serviceMappingHeaders: [
        { title: 'ID', key: 'id', width: '50px' },
        { title: 'Distributor', key: 'distributor_name', width: '16%' },
        { title: 'Service', key: 'service_name', width: '18%' },
        { title: 'Service Type', key: 'service_type', width: '12%' },
        { title: 'Product', key: 'product_name', width: '18%' },
        { title: 'MVNO', key: 'mvno_id', align: 'center', width: '70px' },
        { title: 'Status', key: 'active', align: 'center', width: '80px' },
        { title: 'Actions', key: 'actions', sortable: false, width: '100px' }
      ],
      showServiceMappingDialog: false,
      isEditingServiceMapping: false,
      savingServiceMapping: false,
      serviceMappingForm: {
        id: null,
        service_id: null,
        distributor_id: null,
        product_id: null,
        mvno_id: null,
        active: true
      },
      showDeleteServiceMappingDialog: false,
      deletingServiceMapping: null,
      deletingServiceMappingLoading: false
    }
  },
  computed: {
    availableDistributorsForAllocation() {
      const allocatedIds = this.currentAllocations.map(a => a.id);
      // Don't show the product owner as an allocation option
      const ownerId = this.allocatingProduct?.reseller_id;
      return this.distributorClients.filter(d =>
        !allocatedIds.includes(d.id) && d.id !== ownerId
      );
    },
    isRoot() {
      return this.currentUser?.admin || this.currentUser?.roles?.includes('root');
    },
    distributorClientsForOwner() {
      // Include current client in the list for product owner selection
      const list = [...this.distributorClients];
      const currentClientId = this.currentUser?.client_id;
      const currentClientName = this.currentUser?.client_name || this.currentUser?.client?.name;

      if (currentClientId && !list.find(d => d.id === currentClientId)) {
        list.unshift({ id: currentClientId, name: currentClientName || `Client #${currentClientId}` });
      }
      return list;
    }
  },
  methods: {
    async loadDistributorClients() {
      try {
        const response = await getDistributorClients(this.token);
        console.log("loadDistributorClients response:", response);
        if (!response.error) {
          this.distributorClients = response.distributors || [];
          console.log("distributorClients loaded:", this.distributorClients);
        }
      } catch (err) {
        log("Error loading distributor clients:", err);
      }
    },

    async loadProducts() {
      this.loading = true;
      try {
        const options = {};
        if (this.selectedDistributor) options.client_id = this.selectedDistributor;
        if (this.searchTerm) options.search_term = this.searchTerm;

        const response = await getProducts(this.token, options);
        console.log("getProducts response:", response);
        console.log("products count:", response.products?.length || 0);
        if (!response.error) {
          this.products = response.products || [];
        } else {
          this.showMessage(response.message || 'Error loading products', 'error');
        }
      } catch (err) {
        log("Error loading products:", err);
        this.showMessage('Error loading products', 'error');
      } finally {
        this.loading = false;
      }
    },

    openCreateDialog() {
      this.editingProduct = null;
      this.productForm = {
        name: '',
        billing_i_product: null,
        reseller_id: this.selectedDistributor || this.currentUser?.client_id || null,
        included_data_mb: null,
        included_sms_count: null,
        included_voice_minutes: null
      };
      this.showProductDialog = true;
    },

    openEditDialog(product) {
      this.editingProduct = product;
      this.productForm = {
        name: product.name,
        billing_i_product: product.billing_i_product,
        reseller_id: product.reseller_id,
        included_data_mb: product.included_data_mb,
        included_sms_count: product.included_sms_count,
        included_voice_minutes: product.included_voice_minutes
      };
      this.showProductDialog = true;
    },

    async saveProduct() {
      this.saving = true;
      try {
        let response;
        if (this.editingProduct) {
          response = await updateProduct(this.token, {
            product_id: this.editingProduct.id,
            name: this.productForm.name,
            billing_i_product: this.productForm.billing_i_product,
            included_data_mb: this.productForm.included_data_mb || null,
            included_sms_count: this.productForm.included_sms_count || null,
            included_voice_minutes: this.productForm.included_voice_minutes || null
          });
        } else {
          response = await createProduct(this.token, this.productForm);
        }

        if (!response.error) {
          this.showMessage(response.message || 'Product saved successfully', 'success');
          this.showProductDialog = false;
          this.loadProducts();
        } else {
          this.showMessage(response.message || 'Error saving product', 'error');
        }
      } catch (err) {
        log("Error saving product:", err);
        this.showMessage('Error saving product', 'error');
      } finally {
        this.saving = false;
      }
    },

    async openAllocateDialog(product) {
      this.allocatingProduct = product;
      this.currentAllocations = [];
      this.newAllocationDistributorId = null;
      this.showAllocateDialog = true;
      this.loadingAllocations = true;

      try {
        // Always reload distributors to ensure fresh data
        await this.loadDistributorClients();

        const response = await getProductDetails(this.token, product.id);
        console.log("getProductDetails response:", response);
        if (!response.error) {
          this.currentAllocations = response.allocated_distributors || [];
        }
      } catch (err) {
        log("Error loading allocations:", err);
      } finally {
        this.loadingAllocations = false;
      }
    },

    async allocateToDistributor() {
      if (!this.newAllocationDistributorId || !this.allocatingProduct) return;

      this.allocating = true;
      try {
        const response = await allocateProductToDistributor(
          this.token,
          this.allocatingProduct.id,
          this.newAllocationDistributorId
        );

        if (!response.error) {
          this.showMessage('Product allocated successfully', 'success');
          // Refresh allocations in dialog
          const dist = this.distributorClients.find(d => d.id === this.newAllocationDistributorId);
          if (dist) {
            this.currentAllocations.push({ id: dist.id, name: dist.name });
          }
          this.newAllocationDistributorId = null;
          // Refresh allocations table
          this.loadAllocations();
        } else {
          this.showMessage(response.message || 'Error allocating product', 'error');
        }
      } catch (err) {
        log("Error allocating product:", err);
        this.showMessage('Error allocating product', 'error');
      } finally {
        this.allocating = false;
      }
    },

    async deallocate(distributorId) {
      try {
        const response = await deallocateProductFromDistributor(
          this.token,
          this.allocatingProduct.id,
          distributorId
        );

        if (!response.error) {
          this.showMessage('Product deallocated successfully', 'success');
          this.currentAllocations = this.currentAllocations.filter(a => a.id !== distributorId);
          // Refresh allocations table
          this.loadAllocations();
        } else {
          this.showMessage(response.message || 'Error deallocating product', 'error');
        }
      } catch (err) {
        log("Error deallocating product:", err);
        this.showMessage('Error deallocating product', 'error');
      }
    },

    async openQuickAllocateDialog() {
      this.quickAllocateDistributorId = null;
      this.quickAllocateProductId = null;
      this.showQuickAllocateDialog = true;

      // Load all distributors and all products for selection
      try {
        const [distributorsResponse, productsResponse] = await Promise.all([
          getDistributorClients(this.token),
          getProducts(this.token, {})
        ]);

        console.log("distributorsResponse:", distributorsResponse);
        console.log("productsResponse:", productsResponse);

        if (!distributorsResponse.error) {
          this.allDistributors = distributorsResponse.distributors || [];
          console.log("allDistributors:", this.allDistributors);
        }
        if (!productsResponse.error) {
          this.allProducts = productsResponse.products || [];
          console.log("allProducts:", this.allProducts);
        }
      } catch (err) {
        log("Error loading data for quick allocate:", err);
        this.showMessage('Error loading data', 'error');
      }
    },

    async executeQuickAllocate() {
      if (!this.quickAllocateDistributorId || !this.quickAllocateProductId) return;

      this.quickAllocating = true;
      try {
        const response = await allocateProductToDistributor(
          this.token,
          this.quickAllocateProductId,
          this.quickAllocateDistributorId
        );

        if (!response.error) {
          this.showMessage('Product allocated successfully', 'success');
          this.showQuickAllocateDialog = false;
          this.loadProducts();
          this.loadAllocations();
        } else {
          this.showMessage(response.message || 'Error allocating product', 'error');
        }
      } catch (err) {
        log("Error in quick allocate:", err);
        this.showMessage('Error allocating product', 'error');
      } finally {
        this.quickAllocating = false;
      }
    },

    confirmDelete(product) {
      this.deletingProduct = product;
      this.showDeleteDialog = true;
    },

    async deleteProduct() {
      if (!this.deletingProduct) return;

      this.deleting = true;
      try {
        const response = await deleteProductApi(this.token, this.deletingProduct.id);

        if (!response.error) {
          this.showMessage('Product deleted successfully', 'success');
          this.showDeleteDialog = false;
          this.loadProducts();
        } else {
          this.showMessage(response.message || 'Error deleting product', 'error');
        }
      } catch (err) {
        log("Error deleting product:", err);
        this.showMessage('Error deleting product', 'error');
      } finally {
        this.deleting = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString();
    },

    showMessage(message, color = 'success') {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.showSnackbar = true;
    },

    // Allocations section methods
    async loadAllocations() {
      this.loadingAllocationsList = true;
      try {
        const options = {};
        if (this.allocationFilterDistributor) options.distributor_id = this.allocationFilterDistributor;
        if (this.allocationFilterProduct) options.product_id = this.allocationFilterProduct;

        const response = await getProductAllocations(this.token, options);
        console.log("getProductAllocations response:", response);
        if (!response.error) {
          this.allocations = response.allocations || [];
        } else {
          this.showMessage(response.message || 'Error loading allocations', 'error');
        }
      } catch (err) {
        log("Error loading allocations:", err);
        this.showMessage('Error loading allocations', 'error');
      } finally {
        this.loadingAllocationsList = false;
      }
    },

    confirmDeallocate(item) {
      this.deallocatingItem = item;
      this.showDeallocateDialog = true;
    },

    async executeDeallocate() {
      if (!this.deallocatingItem) return;

      this.deallocating = true;
      try {
        const response = await deallocateProductFromDistributor(
          this.token,
          this.deallocatingItem.product_id,
          this.deallocatingItem.distributor_id
        );

        if (!response.error) {
          this.showMessage('Allocation removed successfully', 'success');
          this.showDeallocateDialog = false;
          this.loadAllocations();
        } else {
          this.showMessage(response.message || 'Error removing allocation', 'error');
        }
      } catch (err) {
        log("Error removing allocation:", err);
        this.showMessage('Error removing allocation', 'error');
      } finally {
        this.deallocating = false;
      }
    },

    // Service-Product Mappings methods
    async loadServiceMappings() {
      this.loadingServiceMappings = true;
      try {
        const response = await getDistributorServiceProducts(this.token);
        log("getDistributorServiceProducts response:", response);
        if (!response.error) {
          let mappings = response.distributor_service_products || [];
          // Apply filters
          if (this.serviceMappingFilterDistributor) {
            mappings = mappings.filter(m => m.distributor_id === this.serviceMappingFilterDistributor);
          }
          if (this.serviceMappingFilterProduct) {
            mappings = mappings.filter(m => m.product_id === this.serviceMappingFilterProduct);
          }
          this.serviceMappings = mappings;
        } else {
          this.showMessage(response.message || 'Error loading service mappings', 'error');
        }
      } catch (err) {
        log("Error loading service mappings:", err);
        this.showMessage('Error loading service mappings', 'error');
      } finally {
        this.loadingServiceMappings = false;
      }
    },

    openAddServiceMappingDialog() {
      this.isEditingServiceMapping = false;
      this.serviceMappingForm = {
        id: null,
        service_id: null,
        distributor_id: null,
        product_id: null,
        mvno_id: null,
        active: true
      };
      this.showServiceMappingDialog = true;
    },

    openEditServiceMappingDialog(item) {
      this.isEditingServiceMapping = true;
      this.serviceMappingForm = {
        id: item.id,
        service_id: item.service_id,
        distributor_id: item.distributor_id,
        product_id: item.product_id,
        mvno_id: item.mvno_id,
        active: item.active
      };
      this.showServiceMappingDialog = true;
    },

    async saveServiceMapping(data) {
      this.savingServiceMapping = true;
      try {
        let response;
        if (this.isEditingServiceMapping) {
          response = await updateDistributorServiceProduct(this.token, data);
        } else {
          response = await addDistributorServiceProduct(this.token, data);
        }

        if (!response.error) {
          this.showMessage(this.isEditingServiceMapping ? 'Mapping updated successfully' : 'Mapping added successfully', 'success');
          this.showServiceMappingDialog = false;
          await this.loadServiceMappings();
        } else {
          this.showMessage(response.message || 'Error saving mapping', 'error');
        }
      } catch (err) {
        log("Error saving service mapping:", err);
        this.showMessage('Error saving mapping', 'error');
      } finally {
        this.savingServiceMapping = false;
      }
    },

    async toggleServiceMappingActive(item) {
      this.loadingServiceMappings = true;
      try {
        const response = await updateDistributorServiceProduct(this.token, {
          id: item.id,
          active: !item.active
        });

        if (!response.error) {
          this.showMessage(`Mapping ${!item.active ? 'activated' : 'deactivated'} successfully`, 'success');
          await this.loadServiceMappings();
        } else {
          this.showMessage(response.message || 'Error updating mapping', 'error');
        }
      } catch (err) {
        log("Error toggling active status:", err);
        this.showMessage('Error updating mapping', 'error');
      } finally {
        this.loadingServiceMappings = false;
      }
    },

    confirmDeleteServiceMapping(item) {
      this.deletingServiceMapping = item;
      this.showDeleteServiceMappingDialog = true;
    },

    async executeDeleteServiceMapping() {
      if (!this.deletingServiceMapping) return;

      this.deletingServiceMappingLoading = true;
      try {
        const response = await deleteDistributorServiceProduct(this.token, this.deletingServiceMapping.id);

        if (!response.error) {
          this.showMessage('Mapping deleted successfully', 'success');
          this.showDeleteServiceMappingDialog = false;
          this.deletingServiceMapping = null;
          await this.loadServiceMappings();
        } else {
          this.showMessage(response.message || 'Error deleting mapping', 'error');
        }
      } catch (err) {
        log("Error deleting service mapping:", err);
        this.showMessage('Error deleting mapping', 'error');
      } finally {
        this.deletingServiceMappingLoading = false;
      }
    },

    handleServiceMappingSnackbar(payload) {
      this.showMessage(payload.message, payload.color);
    }
  },
  created() {
    const { getLoggedIn, token, user } = useGetLoggedIn();
    getLoggedIn().then(() => {
      this.token = token.value;
      this.currentUser = user.value;
      this.loadDistributorClients();
      this.loadProducts();
      this.loadAllocations();
      this.loadServiceMappings();
    });
  }
});
</script>
